/**
 * WIT-IMPL-M101-PWA-001 — pure helpers for the read-only NDT context and the
 * hold-welding warnings the API ships per joint (contract: Weldit
 * docs/WIT-IMPL-M101-003_report.md §8, PWA-001 report). No NDT scope is ever
 * derived here: every figure comes from `ndt_context` / `hold_warnings` as the
 * server sent them; absent keys (older API) yield nothing to show.
 *
 * ESM module without Vue so node --test can run it (tests/js/ndt-context.test.js).
 */

/** The i18n key of the class axis label for a context / profile. */
export function classAxisKey (ctxOrProfile) {
  const axis = ctxOrProfile && typeof ctxOrProfile === 'object' ? String(ctxOrProfile.class_axis || '') : ''
  if (axis === 'm101') return 'message.weldMapping.ndtContext.classM101'
  if (axis === 'm601') return 'message.weldMapping.ndtContext.classM601'
  return 'message.weldMapping.ndtContext.classDefault'
}

/**
 * Hold time as the sheet prints it. `t(key, params)` is the i18n function.
 * Reason keys are the engine's (hold_reason); the drawing / default branches
 * come from hold_source; nothing registered → «none».
 */
export function holdTimeText (ctx, t) {
  if (!ctx || typeof ctx !== 'object') return ''
  const reason = ctx.hold_reason ? String(ctx.hold_reason) : ''
  const hours = (ctx.hold_hours === null || ctx.hold_hours === undefined) ? null : Number(ctx.hold_hours)
  const source = ctx.hold_source ? String(ctx.hold_source) : ''
  // WIT-FIX-M101-001: g1_fixed_48 (table G.1, fixed 48 h) and material_unlinked (no SMYS: link the material).
  // WIT-IMPL-SVV-R761-002: table23 carries the hours, table23_cooling = only cooling, table23_pending = inputs missing.
  const known = ['pwht', 'default_48', 'smys_le_355', 'smys_le_460_thin', 'smys_missing', 'thickness_missing', 'g1_fixed_48', 'material_unlinked', 'table23_cooling', 'table23_pending']
  if (reason === 'table23' && hours !== null) return t('message.weldMapping.ndtContext.hold.table23', { h: hours })
  if (reason && known.indexOf(reason) >= 0) return t('message.weldMapping.ndtContext.hold.' + reason)
  if (source === 'drawing' && hours !== null) return t('message.weldMapping.ndtContext.hold.drawing', { h: hours })
  if (source === 'default' && hours !== null) return t('message.weldMapping.ndtContext.hold.default', { h: hours })
  if (hours !== null && hours > 0) return t('message.weldMapping.ndtContext.hold.default', { h: hours })
  if (source === 'none' || source === '' || hours === null) return t('message.weldMapping.ndtContext.hold.none')
  return t('message.weldMapping.ndtContext.hold.default', { h: hours })
}

/**
 * The rows of the «NDT-kontekst» panel: [{ key, label, value }] — only rows that
 * carry a value, so an older API or a weld without a resolution shows nothing.
 * `fmt(iso)` formats a date for the earliest-NDT row.
 */
export function ndtContextRows (ctx, t, fmt) {
  if (!ctx || typeof ctx !== 'object') return []
  const rows = []
  const push = (key, label, value) => { if (value !== null && value !== undefined && String(value) !== '') rows.push({ key, label, value: String(value) }) }
  push('class', t(classAxisKey(ctx)), ctx.inspection_class)
  push('joint', t('message.weldMapping.ndtContext.jointType'), ctx.config_label || ctx.config)
  push('weldType', t('message.weldMapping.ndtContext.weldType'), ctx.weld_type)
  if (ctx.penetration_depth_mm !== null && ctx.penetration_depth_mm !== undefined) {
    push('penDepth', t('message.weldMapping.ndtContext.penDepth'), ctx.penetration_depth_mm)
  }
  // The hold row only when the server sent a hold figure at all: an empty
  // object (older API, stripped context) must not print «none registered».
  const hasHold = ['hold_hours', 'hold_reason', 'hold_source'].some(k => Object.prototype.hasOwnProperty.call(ctx, k))
  const hold = hasHold ? holdTimeText(ctx, t) : ''
  if (hold) push('hold', t('message.weldMapping.ndtContext.holdTime'), hold)
  if (ctx.earliest_ndt_at) push('earliest', t('message.weldMapping.ndtContext.earliestNdt'), fmt ? fmt(ctx.earliest_ndt_at) : ctx.earliest_ndt_at)
  if (ctx.state === 'pending' || ctx.state === 'refused') push('state', t('message.weldMapping.ndtContext.status'), t('message.weldMapping.ndtContext.pending'))
  return rows
}

/**
 * One warning line per hold-welding entry, in the existing wps_warnings style.
 * Unknown codes are skipped (forward compatibility: the server may add codes).
 */
export function holdWarningLines (warnings, t, fmt) {
  if (!Array.isArray(warnings)) return []
  const out = []
  for (const w of warnings) {
    if (!w || typeof w !== 'object' || w.code !== 'welding_hold') continue
    const base = w.scope === 'welder'
      ? t('message.weldMapping.weldingHold.welder')
      : t('message.weldMapping.weldingHold.wps', { wps: w.wps_nr || w.wps_id || '' })
    const parts = [base]
    if (w.reason) parts.push(String(w.reason))
    if (w.since) parts.push(t('message.weldMapping.weldingHold.since', { date: fmt ? fmt(w.since) : w.since }))
    out.push(parts.join(' · '))
  }
  return out
}

/** true when a joint carries at least one hold-welding warning. */
export function hasHoldWarning (joint) {
  return !!(joint && Array.isArray(joint.hold_warnings) && joint.hold_warnings.some(w => w && w.code === 'welding_hold'))
}
